import type { AssistantActionBase, AssistantMessage } from '../types';

export type AssistantMessageDecoratorInput<TContext, TCitation, TAction> = {
  message: AssistantMessage<TCitation, TAction>;
  context: TContext;
  history: AssistantMessage<TCitation, TAction>[];
  question?: string;
};

export type AssistantMessageDecorator<TContext, TCitation, TAction> = (
  input: AssistantMessageDecoratorInput<TContext, TCitation, TAction>
) => AssistantMessage<TCitation, TAction>;

export const applyMessageDecorators = <TContext, TCitation, TAction>(
  input: AssistantMessageDecoratorInput<TContext, TCitation, TAction>,
  decorators: AssistantMessageDecorator<TContext, TCitation, TAction>[] = []
): AssistantMessage<TCitation, TAction> => (
  decorators.reduce(
    (message, decorator) => decorator({ ...input, message }),
    input.message
  )
);

export type KeywordActionRule<TAction> = {
  keywords: string[];
  actions: TAction[];
  match?: 'any' | 'all';
};

export type KeywordActionDecoratorOptions<TContext, TCitation, TAction> = {
  rules: KeywordActionRule<TAction>[];
  fallbackActions?: TAction[];
  includeQuestion?: boolean;
  shouldDecorate?: (input: AssistantMessageDecoratorInput<TContext, TCitation, TAction>) => boolean;
};

const normalizeText = (value: string) => value.trim().toLowerCase();

const matchesRule = (text: string, rule: KeywordActionRule<unknown>) => {
  const keywords = rule.keywords.map(normalizeText).filter(Boolean);
  if (keywords.length === 0) {
    return false;
  }
  const matchType = rule.match ?? 'any';
  if (matchType === 'all') {
    return keywords.every((keyword) => text.includes(keyword));
  }
  return keywords.some((keyword) => text.includes(keyword));
};

export const createKeywordActionDecorator = <TContext, TCitation, TAction extends AssistantActionBase>(
  options: KeywordActionDecoratorOptions<TContext, TCitation, TAction>
): AssistantMessageDecorator<TContext, TCitation, TAction> => {
  const { rules, fallbackActions, includeQuestion = true, shouldDecorate } = options;
  return (input) => {
    if (input.message.actions && input.message.actions.length > 0) {
      return input.message;
    }
    if (shouldDecorate && !shouldDecorate(input)) {
      return input.message;
    }
    const questionText = includeQuestion ? ` ${input.question ?? ''}` : '';
    const combined = normalizeText(`${input.message.content}${questionText}`);
    if (!combined) {
      return input.message;
    }
    for (const rule of rules) {
      if (matchesRule(combined, rule)) {
        return {
          ...input.message,
          actions: rule.actions,
        };
      }
    }
    if (fallbackActions && fallbackActions.length > 0) {
      return {
        ...input.message,
        actions: fallbackActions,
      };
    }
    return input.message;
  };
};
